<template>
  <b-modal
    id="verlinkungenBearbeitenModal"
    ref="modal"
    v-model="visible"
    content-class="modal-form"
    centered
    no-close-on-backdrop
    @hidden="$emit('close')"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center w-100">
        <Link :size="18" class="mr-2" style="color: #3699ff" />
        <span class="modal-title">Verlinkungen</span>
        <span class="anzahl-badge ml-2">{{ verlinkungen.length }}</span>
        <div class="ml-auto d-flex align-items-center">
          <b-button variant="link" class="hinzufuegen-btn p-0 mr-4" @click="addVerlinkung">
            <CirclePlus :size="16" class="mr-1" />
            Hinzufügen
          </b-button>
          <X :size="20" style="cursor: pointer; color: #99a1af" @click="close()" />
        </div>
      </div>
    </template>

    <VerlinkungenEditor ref="editor" v-model="verlinkungen" hide-add-button @valid="isValid = $event" />

    <template #modal-footer="{ cancel }">
      <b-button class="dialog-btn-secondary" @click="cancel()">Abbrechen</b-button>
      <b-button
        variant="primary"
        :disabled="!isValid || !hasChanges || isSaving"
        class="dialog-btn"
        @click="onSpeichern"
      >
        Speichern
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { Link, CirclePlus, X } from 'lucide-vue';
import VerlinkungenEditor from '@/components/aufgaben/verlinkungen-editor.vue';
import apiService from '@/core/common/services/api.service';

export default {
  name: 'VerlinkungenBearbeitenModal',
  components: {
    VerlinkungenEditor,
    Link,
    CirclePlus,
    X,
  },
  props: {
    aufgabeId: {
      type: [Number, String],
      default: null,
    },
    // Bereits bestehende Verlinkungen der Aufgabe als Editor-Zeilen
    initialVerlinkungen: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      visible: true,
      verlinkungen: [],
      initialRows: [],
      isValid: true,
      isSaving: false,
    };
  },
  created() {
    // Ausgangszustand merken und die Zeilen damit vorbefüllen
    this.initialRows = this.initialVerlinkungen.map((verlinkung, index) => ({ id: index, ...verlinkung }));
    this.verlinkungen = this.initialRows.length
      ? this.initialRows.map(row => ({ ...row }))
      : [{ id: 0, typ: null, artifactId: null, artifactText: null }];
  },
  computed: {
    hasChanges() {
      const gueltigeKeys = new Set(
        this.verlinkungen.filter(v => v.typ && v.artifactId != null).map(this.verlinkungKey)
      );
      const initialeKeys = new Set(this.initialRows.map(this.verlinkungKey));
      if (gueltigeKeys.size !== initialeKeys.size) return true;
      return [...initialeKeys].some(key => !gueltigeKeys.has(key));
    },
  },
  methods: {
    addVerlinkung() {
      this.$refs.editor.addRow();
    },
    verlinkungKey(verlinkung) {
      return verlinkung.typ + '-' + verlinkung.artifactId;
    },
    // Ermittelt den Unterschied zum Ausgangszustand und wendet ihn an
    async onSpeichern() {
      const gueltige = this.verlinkungen.filter(v => v.typ && v.artifactId != null);
      const aktuelleKeys = new Set(gueltige.map(this.verlinkungKey));
      const initialeKeys = new Set(this.initialRows.map(this.verlinkungKey));

      const hinzugefuegt = gueltige.filter(v => !initialeKeys.has(this.verlinkungKey(v)));
      const entfernt = this.initialRows.filter(v => !aktuelleKeys.has(this.verlinkungKey(v)));

      this.isSaving = true;
      try {
        if (entfernt.length) {
          await apiService.delete('/aufgaben/verlinkungen', this.buildPayload(entfernt));
        }
        if (hinzugefuegt.length) {
          await apiService.post('/aufgaben/verlinkungen', this.buildPayload(hinzugefuegt));
        }
        this.$emit('saved');
        this.close();
      } catch {
        this.$bvToast.toast('Die Verlinkungen konnten nicht gespeichert werden.', {
          title: 'Fehler',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.isSaving = false;
      }
    },
    buildPayload(rows) {
      const byTyp = typ => rows.filter(row => row.typ === typ);

      return {
        aufgabeId: this.aufgabeId,
        bnrs: byTyp('Vorgang').map(row => parseInt(row.artifactId)),
        reiseterminkuerzel: byTyp('Reisetermin').map(row => row.artifactText),
        reisekuerzel: byTyp('Reise').map(row => row.artifactText),
      };
    },
    close() {
      this.visible = false;
    },
  },
};
</script>

<style scoped>
.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text);
}

.anzahl-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 11px;
  background-color: #eff6ff;
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
}

.hinzufuegen-btn {
  display: inline-flex;
  align-items: center;
  color: var(--primary);
  font-family: Poppins, Helvetica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  text-decoration: none;
}

.hinzufuegen-btn:hover {
  color: #1086ff;
  text-decoration: none;
}

:deep(.modal-dialog) {
  max-width: 562px;
}
</style>
